import React, { useState, useEffect, useCallback } from 'react';
import { format, parseISO } from 'date-fns';
import { 
  Clock, Loader2, AlertTriangle, CheckCircle, XCircle, 
  LogIn, LogOut, RefreshCw, User, Calendar, MapPin
} from 'lucide-react';
import { api } from '../../services/api';
import AdminLayout from '../../components/layout/AdminLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useToast } from '../../hooks/use-toast';

// Approval Card Component
const ApprovalCard = ({ record, pendingApproval, onApprove, onReject, processingId }) => {
  const [showAdjust, setShowAdjust] = useState(false);
  const [adjustedTime, setAdjustedTime] = useState('');
  
  const isClockIn = pendingApproval.type === 'clock_in';
  const isProcessing = processingId === `${record.id}-${pendingApproval.type}`;
  
  const handleApprove = () => {
    onApprove(record.id, pendingApproval.type, adjustedTime || null);
    setAdjustedTime('');
    setShowAdjust(false);
  };
  
  const handleReject = () => {
    onReject(record.id, pendingApproval.type);
  };
  
  return (
    <div 
      className="p-4 rounded-xl border bg-white border-slate-200 hover:shadow-md transition-all"
      data-testid={`approval-card-${record.id}-${pendingApproval.type}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold ${
            isClockIn ? 'bg-gradient-to-br from-amber-500 to-orange-600' : 'bg-gradient-to-br from-purple-500 to-indigo-600'
          }`}>
            {isClockIn ? <LogIn className="w-6 h-6" /> : <LogOut className="w-6 h-6" />}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className="font-semibold text-slate-800">{record.guard_name || 'Unknown Guard'}</p>
              <Badge className={`text-xs ${isClockIn ? 'bg-amber-100 text-amber-700' : 'bg-purple-100 text-purple-700'}`}>
                {isClockIn ? 'Clock-in' : 'Clock-out'}
              </Badge>
            </div>
            <p className="text-sm text-slate-500">{pendingApproval.reason}</p>
          </div>
        </div>
        
        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
          <AlertTriangle className="w-3 h-3 mr-1" />
          Pending
        </Badge>
      </div>
      
      {/* Details Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="bg-slate-50 rounded-lg p-3">
          <p className="text-[10px] uppercase tracking-wide text-slate-400 font-medium flex items-center gap-1">
            <Calendar className="w-3 h-3" /> Date
          </p>
          <p className="text-sm font-semibold text-slate-700">
            {record.date ? format(parseISO(record.date), 'MMM d, yyyy') : '—'}
          </p>
        </div>
        <div className="bg-slate-50 rounded-lg p-3">
          <p className="text-[10px] uppercase tracking-wide text-slate-400 font-medium flex items-center gap-1">
            <Clock className="w-3 h-3" /> Scheduled
          </p>
          <p className="text-sm font-semibold text-slate-700">
            {isClockIn ? (record.scheduled_start || '—') : (record.scheduled_end || '—')}
          </p>
        </div>
        <div className="bg-slate-50 rounded-lg p-3">
          <p className="text-[10px] uppercase tracking-wide text-slate-400 font-medium flex items-center gap-1">
            <Clock className="w-3 h-3" /> Actual
          </p>
          <p className="text-sm font-semibold text-amber-600">
            {pendingApproval.actual_time 
              ? format(parseISO(pendingApproval.actual_time), 'HH:mm')
              : '—'}
          </p>
        </div>
        <div className="bg-slate-50 rounded-lg p-3">
          <p className="text-[10px] uppercase tracking-wide text-slate-400 font-medium flex items-center gap-1">
            <MapPin className="w-3 h-3" /> Site
          </p>
          <p className="text-sm font-semibold text-slate-700 truncate">
            {record.site_name || '—'}
          </p>
        </div>
      </div>
      
      {/* Time Adjustment (optional) */}
      {showAdjust && (
        <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-sm font-medium text-blue-800 mb-2 flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Adjust {isClockIn ? 'Clock-in' : 'Clock-out'} Time
          </p>
          <Input
            type="datetime-local"
            value={adjustedTime}
            onChange={(e) => setAdjustedTime(e.target.value)}
            className="bg-white"
            placeholder="Leave empty to approve with original time"
          />
          <p className="text-xs text-slate-500 mt-2">
            Leave empty to approve with original actual time. Adjusted time will be used for payroll calculation.
          </p>
        </div>
      )}
      
      {/* Actions */}
      <div className="flex items-center justify-between pt-3 border-t border-slate-200">
        <Button
          size="sm"
          variant="ghost"
          className="text-blue-600 hover:bg-blue-50 text-xs"
          onClick={() => setShowAdjust(!showAdjust)}
          disabled={isProcessing}
        >
          <Clock className="w-3 h-3 mr-1" />
          {showAdjust ? 'Hide Adjustment' : 'Adjust Time'}
        </Button>
        
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            className="text-red-600 hover:bg-red-50 border-red-200"
            onClick={handleReject}
            disabled={isProcessing}
          >
            <XCircle className="w-4 h-4 mr-1" /> Reject
          </Button>
          <Button
            size="sm"
            className="bg-green-600 hover:bg-green-700"
            onClick={handleApprove}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <Loader2 className="w-4 h-4 animate-spin mr-1" />
            ) : (
              <CheckCircle className="w-4 h-4 mr-1" />
            )}
            {showAdjust && adjustedTime ? 'Approve (Adjusted)' : 'Approve'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default function AdminApprovals() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [approvals, setApprovals] = useState([]);
  const [processingId, setProcessingId] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  
  const loadApprovals = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getPendingApprovals();
      setApprovals(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading approvals:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load pending approvals"
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadApprovals();
  }, [loadApprovals]);

  // Flatten approvals into individual approval items
  const approvalItems = approvals.flatMap(record => 
    (record.pending_approvals || []).map(pa => ({
      record,
      pendingApproval: pa
    }))
  );
  
  // Filter by tab
  const filteredItems = approvalItems.filter(item => {
    if (activeTab === 'all') return true;
    if (activeTab === 'clock_in') return item.pendingApproval.type === 'clock_in';
    if (activeTab === 'clock_out') return item.pendingApproval.type === 'clock_out';
    return true;
  });
  
  // Counts
  const clockInCount = approvalItems.filter(i => i.pendingApproval.type === 'clock_in').length;
  const clockOutCount = approvalItems.filter(i => i.pendingApproval.type === 'clock_out').length;

  const handleApprove = async (attendanceId, approvalType, adjustedTime) => {
    setProcessingId(`${attendanceId}-${approvalType}`);
    try {
      await api.approveAttendanceAction(attendanceId, approvalType, 'approved', null, adjustedTime);
      toast({
        title: "Approved",
        description: `${approvalType === 'clock_in' ? 'Clock-in' : 'Clock-out'} has been approved${adjustedTime ? ' with time adjustment' : ''}`,
      });
      loadApprovals();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || 'Failed to approve',
      });
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (attendanceId, approvalType) => {
    setProcessingId(`${attendanceId}-${approvalType}`);
    try {
      await api.approveAttendanceAction(attendanceId, approvalType, 'rejected', null, null);
      toast({
        title: "Rejected",
        description: `${approvalType === 'clock_in' ? 'Clock-in' : 'Clock-out'} has been rejected`,
      });
      loadApprovals();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || 'Failed to reject',
      });
    } finally {
      setProcessingId(null);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Loader2 className="w-10 h-10 animate-spin text-blue-600 mx-auto" />
            <p className="mt-3 text-slate-500">Loading pending approvals...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-approvals-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Attendance Approvals</h1>
            <p className="text-slate-500 text-sm">
              Review and approve late clock-ins and early/late clock-outs
            </p>
          </div>
          <Button 
            variant="outline" 
            onClick={loadApprovals}
            className="gap-2"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </Button>
        </div>

        {/* Info Card */}
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-blue-800">10-Minute Approval Rule</p>
                <p className="text-sm text-blue-700 mt-1">
                  Guards who clock in/out <strong>more than 10 minutes</strong> from their scheduled time require approval. 
                  Those within 10 minutes are auto-approved, and scheduled time is used for payroll calculation.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Summary */}
        <div className="grid grid-cols-3 gap-4">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{approvalItems.length}</p>
                  <p className="text-sm text-slate-500">Total Pending</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <LogIn className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{clockInCount}</p>
                  <p className="text-sm text-slate-500">Late Clock-ins</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <LogOut className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{clockOutCount}</p>
                  <p className="text-sm text-slate-500">Early/Late Clock-outs</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs & List */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-100">
            <TabsTrigger value="all" className="gap-2">
              All <Badge variant="secondary">{approvalItems.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="clock_in" className="gap-2">
              <LogIn className="w-4 h-4" /> Clock-ins <Badge variant="secondary">{clockInCount}</Badge>
            </TabsTrigger>
            <TabsTrigger value="clock_out" className="gap-2">
              <LogOut className="w-4 h-4" /> Clock-outs <Badge variant="secondary">{clockOutCount}</Badge>
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-4 space-y-3">
            {filteredItems.length === 0 ? (
              <Card className="border-0 shadow-sm">
                <CardContent className="p-12 text-center">
                  <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-200" />
                  <p className="text-lg font-medium text-slate-600">All caught up!</p>
                  <p className="text-sm text-slate-400 mt-1">
                    No pending approvals at the moment
                  </p>
                </CardContent>
              </Card>
            ) : (
              filteredItems.map((item, idx) => (
                <ApprovalCard
                  key={`${item.record.id}-${item.pendingApproval.type}-${idx}`}
                  record={item.record}
                  pendingApproval={item.pendingApproval}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  processingId={processingId}
                />
              ))
            )}
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}
